/**
 * TradeMatchCard — the "Trade Match" surface at the top of the Proposed pane.
 *
 * The card highlights the single most-suitable swap built from the trainer's
 * OWN trade list (see selectTradeMatchHighlight, fed by useTradeHubData), with
 * a "You give / You get" hero and the two ways to put it to the friend. It is
 * fully reactive: the parent recomputes the highlight on every trade-list,
 * filter and PROPOSAL change, and the hero follows immediately — which is how
 * asking for a swap rolls the card on to the next one.
 *
 * The give/get sprites use the SAME WishlistSprite the Wish List renders with,
 * so a Shiny/Mega/G-Max/form swap shows the right variant art here too.
 *
 * The old /api/trade/candidates ranker (the "Top trade matches" list, its
 * confirm sheet, and the Pro lock that came with it) was retired: the Trade
 * Match is now driven entirely by the user's curated trade list.
 */
import { useMemo } from "react";
import { speciesName } from "@/data/speciesNames";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import { ChanceOnlyBadge } from "@/components/trade/ChanceOnlyBadge";
import { WishlistSprite } from "@/components/WishlistSprite";
import {
  VariantBadge,
  variantColor,
  isDexVariantKey,
  type DexVariantKey,
} from "@/components/dexVariantVisuals";
import type { TradeHighlight, TradeSide } from "@/lib/tradeMatchModel";
import { tradePairingKey, useTradeShareLog } from "@/lib/tradeShareLog";
import {
  shareMethodAlpha,
  TRADE_SHARE_METHODS,
  type TradeShareMethod,
} from "@/lib/tradeShareMethods";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";


/**
 * One share action, drawn in ITS OWN METHOD'S COLOUR.
 *
 * The two used to be an accent-green slab and an accent-green outline — the
 * same colour, differing only in weight, for two acts with very different
 * consequences (one puts a real proposal to another trainer; one saves a
 * picture). They now take the colour and glyph their method owns everywhere
 * else, including on the trade that comes out the other end, so the button you
 * pressed and the row you got are visibly the same thing.
 *
 * WEIGHT STILL SEPARATES THEM. `solid` is the act the card exists for — putting
 * the swap to this trainer — and the other is its quieter sibling. Colour says
 * WHICH, weight says WHICH FIRST; dropping either one flattens a real
 * difference.
 *
 * `done` marks a method already used for this swap: a stronger tint and a tick.
 * It stays a live button rather than becoming a label — the same card can be
 * sent to somebody else.
 */
function MatchAction({
  testId,
  onClick,
  method,
  solid = false,
  done = false,
}: {
  testId: string;
  onClick: () => void;
  method: TradeShareMethod;
  solid?: boolean;
  done?: boolean;
}) {
  const meta = TRADE_SHARE_METHODS[method];
  const { Icon } = meta;
  const style = solid
    ? {
        background: `linear-gradient(180deg, ${meta.color} 0%, color-mix(in srgb, ${meta.color} 88%, black) 100%)`,
        border: `1px solid ${meta.color}`,
        color: "var(--text-on-accent)",
      }
    : {
        background: shareMethodAlpha(method, done ? 0.24 : 0.12),
        border: `1px solid ${shareMethodAlpha(method, done ? 0.85 : 0.5)}`,
        color: meta.color,
      };
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      data-share-method={method}
      data-share-done={done || undefined}
      className="rounded-[var(--radius-lg)] px-3 py-2 min-h-[44px] inline-flex flex-col items-center justify-center gap-0.5 hover-elevate active-elevate-2"
      style={style}
    >
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold leading-none">
        {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
        {meta.label}
      </span>
      <span className="text-[9.5px] font-semibold opacity-75 leading-none truncate max-w-full">
        {done ? meta.chip : meta.hint}
      </span>
    </button>
  );
}

export function TradeMatchCard({
  friendName,
  highlight,
  allProposed = false,
  onPropose,
  onExport,
}: {
  friendName: string;
  /**
   * The user's earmarked-trade highlight (the most-suitable give+get swap from
   * their trade list, skipping anything already proposed). Drives the "You give
   * / You get" hero and the CTAs; null when there is nothing left to pair.
   */
  highlight?: TradeHighlight | null;
  /**
   * True when the list DOES have both sides but every pairing is already in
   * flight — a different empty state from having earmarked nothing at all.
   */
  allProposed?: boolean;
  /** Puts the swap to the friend IN-APP — creates a proposal they can agree to. */
  onPropose?: () => void;
  /** Renders the branded trade card and opens the native share sheet. */
  onExport?: () => void;
}) {
  // Whether THIS pairing has already gone out as a card from this device. Live,
  // so the button changes the moment the share sheet is handed the image.
  const shareLog = useTradeShareLog();
  const sharedAsCard = highlight != null && shareLog.has(tradePairingKey(highlight));

  // Tone comes from the "get" side's dex variant so the hero reads in the same
  // colour language as the rest of the page's variant badges.
  const tone = useMemo(() => {
    if (!highlight) return "hsl(142 78% 56%)";
    const key = isDexVariantKey(highlight.get.variant)
      ? (highlight.get.variant as DexVariantKey)
      : "pokemon";
    return variantColor(key);
  }, [highlight]);

  return (
    <section className="mt-5 v2-neon-card-soft p-4" data-testid="section-trade-match">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-3.5 h-3.5" style={{ color: "hsl(142 100% 60%)" }} />
        <h2 className="v2-display-up text-[11px]" style={{ color: "var(--accent-fill)" }}>
          Trade Match
        </h2>
      </div>
      <p className="text-[10.5px] mb-3" style={{ color: "var(--text-secondary)" }}>
        Your best swap with {friendName}, built from your trade list.
      </p>

      {!highlight ? (
        <div className="text-center py-6 px-2" data-testid="empty-trade-match">
          <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {allProposed
              ? `Every swap you've earmarked with ${friendName} is already proposed. They're waiting just below. Earmark another give or get and the next match shows here.`
              : `No trade match yet. Earmark a give and a get from the lists below and your best swap with ${friendName} shows here.`}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-stretch gap-2 mb-4" data-testid="trade-match-hero">
            <GiveGetCard title="You give" side={highlight.give} tone={tone} />
            <div className="flex flex-col items-center justify-center shrink-0 px-0.5">
              <ArrowRight className="w-4 h-4" style={{ color: tone }} />
              <ArrowRight className="w-4 h-4 -mt-1.5" style={{ color: tone.replace(")", " / 0.5)") }} />
            </div>
            <GiveGetCard title="You get" side={highlight.get} tone={tone} />
          </div>

          {/*
            THE ACTION ROW, rebuilt 2026-08-12.

            It was two identical neon slabs reading "Mark traded" and "Share
            trade", the first wearing a paper-plane SEND icon — the universal
            glyph for "this goes to someone else", on the one button that goes
            nowhere and writes your own dex. Two equal-weight buttons also made
            the destructive-ish one (which stamps the trade as done) look like
            the safe one.

            Now: the two SHARE actions lead, because sharing is what you do
            first and they are what the card is for, and they are split — asking
            in-app is a different act from exporting a picture, and one button
            cannot mean both.

            "Already traded it" then went too (2026-08-12). Asking puts the swap
            in the proposals list below, where agreeing files it and cancelling
            takes it back — so the card had a third button that finished a trade
            behind that list's back, with no undo, on a pairing the list was
            already tracking. One swap, two places to end it, is one too many.

            And each now wears its own method's colour and glyph, because same-
            coloured buttons for "put this to a trainer" and "save a picture"
            made two different acts look like one with a style variation.
          */}
          <div className="grid grid-cols-2 gap-2">
            {onPropose && (
              <MatchAction testId="button-propose-trade" onClick={onPropose} method="inApp" solid />
            )}
            {onExport && (
              <MatchAction
                testId="button-export-trade"
                onClick={onExport}
                method="card"
                done={sharedAsCard}
              />
            )}
          </div>
          {/* A swap sent out as a card but never put to the trainer in app is a
              trade nobody has agreed to — say so, since the proposals list below
              cannot: it has no row for it. */}
          {sharedAsCard && onPropose && (
            <p
              className="mt-2 text-[10.5px] leading-snug"
              style={{ color: "var(--text-secondary)" }}
              data-testid="trade-match-card-shared-note"
            >
              You've sent this one as a card. Ask {friendName} in app too and it becomes a proposal
              they can agree to.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One side of the hero swap. The sprite is the WishlistSprite — identical art to
 * the Wish List and Compare wish rows — so the correct variant (Shiny / Mega /
 * G-Max / a specific form) is surfaced, and the dex-variant badge sits beneath.
 */
function GiveGetCard({ title, side, tone }: { title: string; side: TradeSide; tone: string }) {
  const variantKey = isDexVariantKey(side.variant) ? (side.variant as DexVariantKey) : null;
  return (
    <div
      className="flex-1 min-w-0 rounded-2xl p-3 flex flex-col items-center text-center"
      style={{
        background: "var(--surface-dark-6)",
        border: `1.5px solid ${tone.replace(")", " / 0.45)")}`,
        boxShadow: `0 0 14px ${tone.replace(")", " / 0.2)")}`,
      }}
    >
      <span className="text-[9px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>
        {title}
      </span>
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-1.5 overflow-clip p-1"
        style={{ background: "var(--surface-dark-4)", border: `1px solid ${tone.replace(")", " / 0.3)")}` }}
      >
        <WishlistSprite
          entry={{ variant: side.variant as PokedexVariant, dexNumber: side.speciesId, addedAt: 0 }}
          fill
          ariaLabel={speciesName(side.speciesId)}
        />
      </div>
      <p className="text-[12px] font-bold leading-tight truncate w-full" style={{ color: "var(--text-primary)" }}>
        {speciesName(side.speciesId)}
      </p>
      {variantKey && (
        <div className="mt-1 flex flex-col items-center gap-1">
          <VariantBadge variant={variantKey} />
          {/* The headline swap has the most to gain from the caveat: this is
              the row a trainer acts on. See lib/tradeChance. */}
          <ChanceOnlyBadge variant={side.variant} />
        </div>
      )}
    </div>
  );
}
